import '@mdxeditor/editor/style.css'

import ArrowLeft from 'components/ReusableComponents/Icons/ArrowLeft'
import TextArea from 'components/ReusableComponents/ManageHotel/TextArea'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { ColorInput, ImageFileInput } from 'features/ConfigureWidgets'
import { getEmailImageUploadConfig } from 'features/EmailEditor/emailImageUploadHelpers'
import {
  getTemplateSectionLabel,
  getTemplateSectionProperties
} from 'features/EmailEditor/templateSectionProperties'
import {
  propertyNames,
  propertyTypes
} from 'features/EmailEditor/constants'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import { resolveEditorRichtextFields } from 'features/EmailEditor/campaignEmailCustomizationHelpers'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import RichTextEditor from 'features/EmailEditor/RichTextEditor'
import EmailTemplatePicker from 'features/EmailEditor/EmailTemplatePicker'
import TestEmail from 'features/EmailEditor/TestEmail'
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark'

import styles from './EmailEditor.module.scss'

const EmailEditorContent = () => {
  const {
    editingSection,
    setEditingSection,
    selectedTemplate,
    emailState,
    updateEmailState,
    updateRichTextField,
    handleTemplateChange,
    handleSocialMediaChange,
    editorHydrationKey,
    currentTemplate,
    resolveEditorImageField
  } = useEmailEditor()

  const resolvedRichtextFields = resolveEditorRichtextFields(
    currentTemplate.params,
    emailState.richtextFields || {}
  )

  return (
    <div>
      <div className={styles.editorWrapper}>
        <div className={styles.inputs}>
          <div className={styles.inputsHeader}>
            {editingSection && (
              <>
                <button
                  className={styles.backButton}
                  onClick={() => setEditingSection(null)}
                >
                  <ArrowLeft /> <span>Back to email settings</span>
                </button>
                <span className={style.sectionTitle}>Editing {editingSection}</span>
              </>
            )}
            {!editingSection && (
              <span className={style.sectionTitle}>Email settings</span>
            )}
          </div>
          <div className={styles.inputsInner}>
            {!editingSection && (
              <EmailTemplatePicker
                selectedTemplate={selectedTemplate}
                onTemplateChange={handleTemplateChange}
              />
            )}

            {/* Email Editor Inputs according to the active section */}
            {getTemplateSectionProperties(selectedTemplate, editingSection || 'global').map(property => {
              const type = propertyTypes[property]
              if (type === 'color') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <ColorInput
                      title={propertyNames[property]}
                      height={32}
                      defaultColor={emailState[property]}
                      onChange={value => updateEmailState(property, value)}
                    />
                  </div>
                )
              } else if (type === 'socialmedia') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <span style={{ display: 'inline-block', width: '200px' }}>
                      {propertyNames[property]}:
                    </span>
                    {emailState.socialMediaLinks.map((link, index) => (
                      <TextInput
                        key={index}
                        label={link.platform}
                        value={link.url}
                        onChange={e =>
                          handleSocialMediaChange(index, 'url', e.target.value)
                        }
                      />
                    ))}
                  </div>
                )
              } else if (type === 'image') {
                const uploadConfig = getEmailImageUploadConfig(property)

                return (
                  <ImageFileInput
                    key={property}
                    kind={uploadConfig.kind}
                    title={propertyNames[property]}
                    backgroundSize={property.includes('logo') ? '90%' : 'cover'}
                    defaultUrl={resolveEditorImageField(property) || ''}
                    onChange={text => {
                      updateEmailState(property, text)
                    }}
                    minWidth={uploadConfig.minWidth}
                    minHeight={uploadConfig.minHeight}
                  />
                )
              } else if (type === 'richtext') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <RichTextEditor
                      key={`${property}-${selectedTemplate}-${editorHydrationKey}-${editingSection || 'global'}`}
                      editorKey={`${property}-${selectedTemplate}-${editorHydrationKey}-${editingSection || 'global'}`}
                      label={propertyNames[property]}
                      value={resolvedRichtextFields[property] || ''}
                      onChange={value => updateRichTextField(property, value)}
                    />
                  </div>
                )
              } else if (type === 'multilinetext') {
                return (
                  <div key={property} className={styles.inputGroup}>
                    <TextArea
                      label={propertyNames[property]}
                      value={emailState.preheaderText}
                      onChange={value => updateEmailState(property, value)}
                      multiline={true}
                    />
                  </div>
                )
              } else {
                // text
                return (
                  <div key={property} className={styles.inputGroup}>
                    <TextInput
                      label={propertyNames[property]}
                      value={emailState[property]}
                      onChangeValue={text => {
                        updateEmailState(property, text)
                      }}
                    />
                  </div>
                )
              }
            })}

            {!editingSection && <TestEmail />}
          </div>
        </div>

        <>
          <EmailPreview />
        </>
      </div>
    </div>
  )
}

// Wrapper component that provides the context
const EmailEditor = () => {
  return (
    <EmailEditorProvider>
      <EmailEditorContent />
    </EmailEditorProvider>
  )
}

export default EmailEditor
